import React, { useState } from "react";
import { Field, FieldArray } from "redux-form";
import { components } from "react-select";

import { customInput } from "../../../../common/custom-input";
import { customTextarea } from "../../../../common/customTextarea";
import CustomDatePickerV2 from "../../../../common/new-core-ui/CustomDatePickerV2";
import { CustomCreatableSingleSelect, CustomSingleSelect } from "../../../../common/new-core-ui/CustomSingleSelect";
import { CustomSwitch } from "../../../../common/customSwitch";
import DocumentImageContainerWithNotes from "./DocumentImageContainerWithNotes";
import PreviewDocument from "./PreviewDocument";

import colourStyles from "./swmsSelectStyles";
import styles from "../../JobStyle.module.css";
import v2Styles from "../../../v2Styles.module.css";
import "./JobSwmsFormStyle.css";

const getStatusColor = (status) => {
  const statusLower = status ? status.toLowerCase() : 'draft';
  switch (statusLower) {
    case 'approved':
    case 'active':
      return { bg: '#D1FAE5', color: '#065F46', text: 'Approved' };
    case 'pending':
      return { bg: '#DBEAFE', color: '#1E40AF', text: 'Pending Approval' };
    case 'rejected':
      return { bg: '#FEE2E2', color: '#991B1B', text: 'Rejected' };
    case 'draft':
    default:
      return { bg: '#FEF3C7', color: '#92400E', text: 'Draft' };
  }
};

const SwmsInfoSection = ({ loading, docStatus = 'draft' }) => {
  const statusStyle = getStatusColor(docStatus);
  const [previewFile, setPreviewFile] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const openFilePreview = (file) => {
    setPreviewFile(file);
    setShowModal(true);
  };

  const closePreview = () => {
    setShowModal(false);
    setPreviewFile(null);
  };

  return (
    <div className="my-2">
      <div className="container-fluid">
        {/* Row 1: SWMS Name, Number, Version */}
        <div className="row mb-2">
          <div className="col-12 col-md-6">
            <div className={styles.Label}>SWMS Name</div>
            <Field name="swms_name" className={styles.TextField} component={customInput} disabled={loading} />
          </div>
          <div className="col-12 col-md-3">
            <div className={styles.Label}>SWMS Number</div>
            <Field name="swms_number" className={styles.TextField} component={customInput} disabled={true} />
          </div>
          <div className="col-12 col-md-3">
            <div className={styles.Label}>SWMS Version</div>
            <Field name="swms_version" className={styles.TextField} component={customInput} disabled={true} />
          </div>
        </div>
        {/* Row 2: Issue Date, Doc Status, Job ID, PO/WO */}
        <div className="row mb-2">
          <div className="col-12 col-md-3">
            <div className={styles.Label}>Issue Date</div>
            <Field
              component={CustomDatePickerV2}
              name="issue_date"
              className="cs-datepicker-v3"
              dropdownClassName="cs-datepicker-dropdown"
              disabled={loading} />
          </div>
          <div className="col-12 col-md-3">
            <div className={styles.Label}>Doc Status</div>
            <div className="d-flex align-items-center pt-1" style={{ gap: '10px' }}>
              <div className="swms-info-status-badge" style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}>
                {statusStyle.text}
              </div>
              <div className="swms-info-auto-updated-label">
                Auto-updated by workflow
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3">
            <div className={styles.Label}>Job ID</div>
            <Field name="job_number" className={styles.TextField} component={customInput} disabled={true} />
          </div>
          <div className="col-12 col-md-3">
            <div className={styles.Label}>PO / WO</div>
            <Field name="po_number" className={styles.TextField} component={customInput} disabled={loading} />
          </div>
        </div>
        {/* Row 3: Scope of Work */}
        <div className="row mb-2">
          <div className="col-12">
            <div className={styles.Label}>Scope of Work</div>
            <Field
              name="scope_of_work"
              className={`${v2Styles.TextArea} ${v2Styles.AutoHeight} ${v2Styles.Rows2}`}
              disabled={loading}
              component={customTextarea}
              autoComplete="off"
            />
          </div>
        </div>
        {/* Row 4: Photos & Attachments */}
        <div className="row mb-2">
          <div className="col-12">
            <FieldArray
              name="documents_images"
              loading={loading}
              previewFile={openFilePreview}
              formName="job_swms_form"
              component={DocumentImageContainerWithNotes}
              s3UploadPath="job-swms/info-section/attachments"
              fieldName="info_section.documents_images"
            />
          </div>
        </div>
        <hr className="swms-info-section-divider" />
        {/* Row 5: QLI Areas */}
        <div className="row mb-2">
          <div className="col-12">
            <div className="d-flex align-items-end mb-1" style={{ gap: '12px' }}>
              <div className={`${styles.Label} mb-0 mr-auto`}>QLI Areas</div>
              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                <span className="swms-info-toggle-hint">Included in document</span>
                <Field
                  name="qli_areas.toggle"
                  size="small"
                  className="cs-switch-v3"
                  component={CustomSwitch}
                  disabled={loading}
                />
              </div>
            </div>
            <Field
              name="qli_areas.value"
              component={CustomSingleSelect}
              styles={colourStyles}
              placeholder=""
              otherProps={{
                isMulti: true,
                hideSelectedOptions: false,
                closeMenuOnSelect: false,
                isDisabled: true,
                noOptionsMessage: () => 'No QLI Area available'
              }} />
          </div>
        </div>
        {/* Row 6: Areas */}
        <div className="row mb-2">
          <div className="col-12">
            <div className="d-flex align-items-end mb-1" style={{ gap: '12px' }}>
              <div className={`${styles.Label} mb-0 mr-auto`}>
                Areas
                <span className="swms-info-type-to-add-badge">Type to Add</span>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                <span className="swms-info-toggle-hint">Included in document</span>
                <Field
                  name="areas.toggle"
                  size="small"
                  className="cs-switch-v3"
                  component={CustomSwitch}
                  disabled={loading}
                />
              </div>
            </div>
            <Field
              name="areas.values"
              component={CustomCreatableSingleSelect}
              styles={colourStyles}
              otherProps={{
                components: { dropdownIndicator: <components.DownChevron /> },
                isMulti: true,
                hideSelectedOptions: false,
                closeMenuOnSelect: false,
                isDisabled: false,
                noOptionsMessage: () => 'Type to add an area'
              }} />
          </div>
        </div>
        {/* Row 7: Site Name & Site Address */}
        <div className="row mb-3">
          <div className="col-12 col-md-4">
            <div className={styles.Label}>Site Name</div>
            <Field name="site_name" className={styles.TextField} component={customInput} disabled={loading} />
          </div>
          <div className="col-12 col-md-8">
            <div className={styles.Label}>Site Address</div>
            <Field name="site_address" className={styles.TextField} component={customInput} disabled={loading} />
          </div>
        </div>
      </div>
      {showModal && previewFile && (
        <PreviewDocument file={previewFile} onCancel={closePreview} />
      )}
    </div>
  );
};

export default SwmsInfoSection;
